import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import api from '../services/api';
import { useAuth } from '../context/AuthContext';

const STATUS_COLORS = { pending:'#f59e0b', printed:'#3b82f6', packed:'#8b5cf6', shipped:'#06b6d4', delivered:'#16a34a', cancelled:'#dc2626' };
const PAY_COLORS = { unpaid:'#ef4444', partial:'#f59e0b', paid:'#16a34a', overpaid:'#8b5cf6' };

export default function Dashboard() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    api.get('/reports/dashboard')
      .then(r => { setData(r.data); setLoading(false); })
      .catch(() => setLoading(false));
  }, []);

  const today = new Date().toLocaleDateString('en-US', { weekday:'long', year:'numeric', month:'long', day:'numeric' });

  const Card = ({ icon, label, value, sub, color, onClick }) => (
    <div onClick={onClick} style={{ background:'#fff', border:'1px solid #e5e7eb', borderRadius:12, padding:'18px 20px', cursor:onClick?'pointer':'default', transition:'box-shadow .15s' }}
      onMouseEnter={e=>onClick&&(e.currentTarget.style.boxShadow='0 4px 12px rgba(0,0,0,.08)')}
      onMouseLeave={e=>e.currentTarget.style.boxShadow='none'}>
      <div style={{ fontSize:22, marginBottom:8 }}>{icon}</div>
      <div style={{ fontSize:26, fontWeight:800, color: color||'#111827' }}>{value??'—'}</div>
      <div style={{ fontSize:13, color:'#6b7280', marginTop:2 }}>{label}</div>
      {sub&&<div style={{ fontSize:11, color:'#9ca3af', marginTop:4 }}>{sub}</div>}
    </div>
  );

  const Section = ({ title, children }) => (
    <div style={{ background:'#fff', border:'1px solid #e5e7eb', borderRadius:12, padding:20, marginBottom:16 }}>
      <div style={{ fontSize:14, fontWeight:700, color:'#111827', marginBottom:14 }}>{title}</div>
      {children}
    </div>
  );

  const Row = ({ label, value, color }) => (
    <div style={{ display:'flex', justifyContent:'space-between', padding:'7px 0', borderBottom:'1px solid #f3f4f6', fontSize:13 }}>
      <span style={{ color:'#6b7280' }}>{label}</span>
      <span style={{ fontWeight:700, color: color||'#111827' }}>{value}</span>
    </div>
  );

  if (loading) return <div style={{ padding:40, color:'#9ca3af', textAlign:'center' }}>Loading...</div>;

  const d = data || {};
  const today_data = d.today || {};
  const month_data = d.month || {};
  const pending = d.pending || {};
  const recent = d.recent_bills || [];
  const low_stock = d.low_stock || [];

  return (
    <div style={{ padding:24, minHeight:'100vh', background:'#f8fafc' }}>
      {/* Header */}
      <div style={{ marginBottom:24 }}>
        <h1 style={{ margin:0, fontSize:22, fontWeight:800, color:'#111827' }}>👋 Welcome back, {user?.username||user?.name}</h1>
        <p style={{ margin:'4px 0 0', fontSize:13, color:'#6b7280' }}>{today}</p>
      </div>

      {/* Today stats */}
      <div style={{ display:'grid', gridTemplateColumns:'repeat(4,1fr)', gap:14, marginBottom:16 }}>
        <Card icon="🧾" label="Today's Bills" value={today_data.bills||0} sub={d.yesterday?.bills>0?`Yesterday: ${d.yesterday.bills}`:null} color="#7c6af5" />
        <Card icon="💰" label="Today's Revenue" value={`$${(today_data.revenue||0).toFixed(2)}`} sub={d.yesterday?.revenue>0?`Yesterday: $${d.yesterday.revenue.toFixed(2)}`:null} color="#16a34a" />
        <Card icon="📦" label="Today's Orders" value={today_data.orders||0} color="#2563eb" />
        <Card icon="💵" label="Today's Collected" value={`$${(today_data.collected||0).toFixed(2)}`} color="#f59e0b" />
      </div>

      {/* Month stats */}
      <div style={{ display:'grid', gridTemplateColumns:'repeat(4,1fr)', gap:14, marginBottom:20 }}>
        <Card icon="📅" label="This Month Bills" value={month_data.bills||0} color="#374151" />
        <Card icon="📈" label="This Month Revenue" value={`$${(month_data.revenue||0).toFixed(2)}`} color="#16a34a" />
        <Card icon="✅" label="This Month Collected" value={`$${(month_data.collected||0).toFixed(2)}`} color="#2563eb" />
        <Card icon="🆕" label="New Customers" value={month_data.new_customers||0} color="#7c6af5" onClick={()=>navigate('/customers')} />
      </div>

      <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr 1fr', gap:16, marginBottom:16 }}>
        {/* Pending queue */}
        <Section title="⏳ Pending Bills">
          <Row label="Total Pending" value={pending.total||0} color="#f59e0b" />
          <Row label="Total Value" value={`$${(pending.total_value||0).toFixed(2)}`} color="#111827" />
          <Row label="PP / COD" value={pending.pp_cod||0} color="#2563eb" />
          <Row label="Province" value={pending.province||0} color="#7c6af5" />
          <Row label="No Contact" value={pending.no_contact||0} color="#dc2626" />
          {pending.no_contact>0&&(
            <button onClick={()=>navigate('/no-contact')} style={{ marginTop:10, width:'100%', background:'#fee2e2', color:'#dc2626', border:'none', borderRadius:6, padding:'7px 0', fontSize:12, fontWeight:600, cursor:'pointer' }}>
              ⚠️ View No Contact Queue
            </button>
          )}
        </Section>

        {/* Bill status breakdown */}
        <Section title="📊 This Month Status">
          {Object.entries(d.status_breakdown||{}).map(([status, count])=>(
            <Row key={status} label={status.charAt(0).toUpperCase()+status.slice(1)} value={count} color={STATUS_COLORS[status]||'#374151'} />
          ))}
        </Section>

        {/* Low stock */}
        <Section title="⚠️ Low Stock Alert">
          {low_stock.length===0&&<div style={{ fontSize:13, color:'#9ca3af', textAlign:'center', padding:'10px 0' }}>✅ All stock levels OK</div>}
          {low_stock.map((s,i)=>(
            <div key={i} style={{ display:'flex', justifyContent:'space-between', padding:'7px 0', borderBottom:'1px solid #f3f4f6', fontSize:12 }}>
              <span style={{ color:'#374151', fontFamily:'monospace' }}>{s.code}</span>
              <span style={{ fontWeight:700, color: s.qty<=1?'#dc2626':'#f59e0b' }}>{s.qty} left</span>
            </div>
          ))}
          {low_stock.length>0&&(
            <button onClick={()=>navigate('/stock')} style={{ marginTop:10, width:'100%', background:'#fef3c7', color:'#92400e', border:'none', borderRadius:6, padding:'7px 0', fontSize:12, fontWeight:600, cursor:'pointer' }}>
              View Stock
            </button>
          )}
        </Section>
      </div>

      {/* Recent bills */}
      <Section title="🕐 Recent Bills">
        <table style={{ width:'100%', borderCollapse:'collapse', fontSize:13 }}>
          <thead>
            <tr style={{ borderBottom:'1px solid #e5e7eb' }}>
              {['Bill No','Customer','Total','Status','Payment','Created'].map(h=>(
                <th key={h} style={{ padding:'6px 10px', textAlign:'left', fontSize:11, fontWeight:600, color:'#6b7280' }}>{h}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {recent.map((b,i)=>(
              <tr key={i} style={{ borderBottom:'1px solid #f3f4f6' }}>
                <td style={{ padding:'7px 10px', fontFamily:'monospace', fontSize:11, color:'#7c6af5', fontWeight:700 }}>{b.bill_number}</td>
                <td style={{ padding:'7px 10px', fontWeight:500 }}>{b.customer}</td>
                <td style={{ padding:'7px 10px', fontWeight:700, color:'#16a34a' }}>${b.total.toFixed(2)}</td>
                <td style={{ padding:'7px 10px' }}><span style={{ fontSize:11, background:(STATUS_COLORS[b.status]||'#9ca3af')+'20', color:STATUS_COLORS[b.status]||'#9ca3af', padding:'2px 8px', borderRadius:8, fontWeight:600 }}>{b.status}</span></td>
                <td style={{ padding:'7px 10px' }}><span style={{ fontSize:11, background:(PAY_COLORS[b.payment_status]||'#9ca3af')+'20', color:PAY_COLORS[b.payment_status]||'#9ca3af', padding:'2px 8px', borderRadius:8, fontWeight:600 }}>{b.payment_status}</span></td>
                <td style={{ padding:'7px 10px', fontSize:11, color:'#9ca3af' }}>{b.created_at?.slice(0,16)}</td>
              </tr>
            ))}
            {recent.length===0&&<tr><td colSpan={6} style={{ padding:20, textAlign:'center', color:'#9ca3af' }}>No bills yet</td></tr>}
          </tbody>
        </table>
        <button onClick={()=>navigate('/bills')} style={{ marginTop:12, background:'#f3f4f6', color:'#374151', border:'none', borderRadius:6, padding:'7px 16px', fontSize:12, fontWeight:600, cursor:'pointer' }}>View All Bills →</button>
      </Section>
    </div>
  );
}
